import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { today } from "../../../util/formatDate";
import { buildPayrollRow } from "../../../util/rekapAbsensiUtils";

// Buang field bernilai default (false / null / 0 / "") dari detail harian
// supaya payload ke /resetPayroll kecil. Field yang hilang dibaca sebagai falsy.
// "tanggal" selalu dipertahankan.
const compactDetail = (detail) =>
  (Array.isArray(detail) ? detail : []).map((d) =>
    Object.fromEntries(
      Object.entries(d).filter(
        ([key, v]) =>
          key === "tanggal" ||
          (v !== false && v !== null && v !== undefined && v !== 0 && v !== "")
      )
    )
  );

export const useRekapAbsensi = () => {
  const getToken = localStorage.getItem("auth")
    ? localStorage.getItem("auth")
    : null;
  const getTim = localStorage.getItem("user");
  const jsonTim = JSON.parse(getTim);

  const [dataRekap, setDataRekap] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [valueSearch, setValueSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resetDate, setResetDate] = useState(today);
  const [popUpResetDate, setPopUpResetDate] = useState(false);
  const [typeKaryawan, setTypeKaryawan] = useState("contract");
  const [resetLoading, setResetLoading] = useState(false);

  const getRekapAbsensi = useCallback(async () => {
    if (!startDate || !endDate) {
      setErrorMessage("Tanggal awal dan tanggal akhir wajib diisi");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/getRekapAbsensi`,
        {
          startDate: startDate,
          endDate: endDate,
          tim: jsonTim.tim,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken}`,
          },
        }
      );

      setDataRekap(response.data.data ? response.data.data : []);
    } catch (error) {
      setErrorMessage("Gagal mengambil data rekap absensi");
      setDataRekap([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, getToken]);

  useEffect(() => {
    getRekapAbsensi();
  }, [startDate, endDate, typeKaryawan]);

  const sortByName = (data) =>
    [...data].sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });

  const searchFeature = () => {
    if (valueSearch === "") return sortByName(dataRekap);

    return sortByName(
      dataRekap.filter((data) => {
        const matchName = data.name
          ? data.name.toLowerCase().includes(valueSearch.toLowerCase())
          : false;
        const matchId = data.userId ? data.userId.includes(valueSearch) : false;
        return matchName || matchId;
      })
    );
  };

  const handleResetPayroll = useCallback(async () => {
    const isRowRelevant = (row) =>
      (row.takeHomePay || 0) > 0 ||
      (row.kurangBayar || 0) > 0 ||
      (row.lebihBayar || 0) > 0;

    const payrollRows = dataRekap
      .map((data) => buildPayrollRow(data, startDate, endDate, typeKaryawan))
      .filter(isRowRelevant)
      .map((row) => ({ ...row, detail: compactDetail(row.detail) }));

    if (payrollRows.length === 0) {
      setErrorMessage("Tidak ada data yang bisa di-reset pada periode ini");
      return;
    }
    if (!resetDate) {
      setErrorMessage("Tanggal reset wajib diisi");
      return;
    }

    setResetLoading(true);
    setErrorMessage("");

    try {
      await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/resetPayroll`,
        {
          userIds: payrollRows.map((row) => row.userId),
          startDate,
          endDate,
          resetDate,
          typeKaryawan,
          tim: jsonTim.tim,
          rows: payrollRows,
          name: jsonTim.tim,
        },
        {
          headers: { Authorization: `Bearer ${getToken}` },
        }
      );

      setPopUpResetDate(false);
      await getRekapAbsensi();
    } catch (error) {
      console.log(error);
      setErrorMessage("Gagal reset payroll");
    } finally {
      setResetLoading(false);
    }
  }, [
    dataRekap,
    startDate,
    endDate,
    typeKaryawan,
    resetDate,
    getToken,
    getRekapAbsensi,
    jsonTim.tim,
  ]);

  return {
    getTim: jsonTim,
    dataRekap: searchFeature(),
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    valueSearch,
    setValueSearch,
    loading,
    errorMessage,
    resetDate,
    setResetDate,
    popUpResetDate,
    setPopUpResetDate,
    typeKaryawan,
    setTypeKaryawan,
    resetLoading,
    handleResetPayroll,
    refetch: getRekapAbsensi,
  };
};
